import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { BlogService } from '../services/blogService';
import type { Blog, BlogUpsertPayload } from '../types/blog';

function splitCsv(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function toDatetimeLocal(iso?: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function BlogEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const blogId = params.id;

  const initialFromState = (location.state as any)?.blog as Blog | undefined;

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const [title, setTitle] = React.useState(initialFromState?.title || '');
  const [slug, setSlug] = React.useState(initialFromState?.slug || '');
  const [excerpt, setExcerpt] = React.useState(initialFromState?.excerpt || '');
  const [coverImageUrl, setCoverImageUrl] = React.useState(initialFromState?.coverImageUrl || '');
  const [categories, setCategories] = React.useState((initialFromState?.categories || []).join(', '));
  const [tags, setTags] = React.useState((initialFromState?.tags || []).join(', '));
  const [authorName, setAuthorName] = React.useState(initialFromState?.author?.name || '');
  const [authorInitials, setAuthorInitials] = React.useState(initialFromState?.author?.initials || '');
  const [status, setStatus] = React.useState<string>(String(initialFromState?.status || 'draft'));
  const [publishedAt, setPublishedAt] = React.useState<string>(toDatetimeLocal(initialFromState?.publishedAt));
  const [contentHtml, setContentHtml] = React.useState(initialFromState?.contentHtml || '');

  React.useEffect(() => {
    document.title = blogId ? 'Edit Blog' : 'New Blog';
  }, [blogId]);

  const buildPayload = (): BlogUpsertPayload => {
    return {
      title: title.trim(),
      slug: slug.trim(),
      excerpt: excerpt.trim() || undefined,
      coverImageUrl: coverImageUrl.trim() || undefined,
      categories: splitCsv(categories),
      tags: splitCsv(tags),
      author: {
        name: authorName.trim() || undefined,
        initials: authorInitials.trim() || undefined,
      },
      status: status as any,
      publishedAt: publishedAt ? new Date(publishedAt).toISOString() : undefined,
      contentHtml: contentHtml || undefined,
    };
  };

  const onSave = async () => {
    setError(null);
    setSuccess(null);

    const payload = buildPayload();
    if (!payload.title) {
      setError('Title is required');
      return;
    }
    if (!payload.slug) {
      setError('Slug is required');
      return;
    }

    try {
      setLoading(true);
      if (blogId) {
        await BlogService.updateBlog(blogId, payload);
        setSuccess('Blog updated');
      } else {
        const created = await BlogService.createBlog(payload);
        setSuccess('Blog created');
        navigate(`/blogs/${encodeURIComponent(created._id)}`, { replace: true, state: { blog: created } });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save blog');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{blogId ? 'Edit Blog' : 'Create Blog'}</h1>
          <p className="mt-1 text-sm text-gray-500">{blogId ? `PUT /api/blog/admin/${blogId}` : 'POST /api/blog/admin'}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/blogs')}
            className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            disabled={loading}
          >
            Back
          </button>
          <button
            onClick={onSave}
            className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-60"
            disabled={loading}
          >
            {loading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {!initialFromState && blogId && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 rounded-md p-4 text-sm">
          This editor was opened without blog data (likely due to page refresh). You can still edit and save, but some fields
          may be blank. Open the blog from the list again for prefilled values.
        </div>
      )}

      {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-800 rounded-md p-4 text-sm">{success}</div>}

      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
              placeholder="Sample Blog Title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Slug</label>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
              placeholder="sample-blog-title"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Excerpt</label>
          <textarea
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
            rows={3}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Cover Image URL</label>
          <input
            value={coverImageUrl}
            onChange={(e) => setCoverImageUrl(e.target.value)}
            className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
            placeholder="https://..."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Categories (comma-separated)</label>
            <input
              value={categories}
              onChange={(e) => setCategories(e.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
              placeholder="Credit Cards, Travel"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Tags (comma-separated)</label>
            <input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
              placeholder="rewards, miles"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Author Name</label>
            <input
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Author Initials</label>
            <input
              value={authorInitials}
              onChange={(e) => setAuthorInitials(e.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
              placeholder="R"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
            >
              <option value="draft">draft</option>
              <option value="published">published</option>
              <option value="archived">archived</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Published At</label>
          <input
            type="datetime-local"
            value={publishedAt}
            onChange={(e) => setPublishedAt(e.target.value)}
            className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
          />
          <p className="mt-1 text-xs text-gray-500">If empty, the backend may treat this as unpublished depending on status.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Content (HTML)</label>
          <textarea
            value={contentHtml}
            onChange={(e) => setContentHtml(e.target.value)}
            className="mt-1 block w-full rounded border border-gray-300 px-3 py-2 font-mono"
            rows={14}
            placeholder="<p>Hello blog</p>"
          />
        </div>
      </div>
    </div>
  );
}
